import { useCallback, useEffect, useRef, useState } from 'react'
import { GlobalEvents } from '@highlight/ipc'
import { useGlobalState, useGlobalStore } from '@highlight/ipc/react'
import { DictationOrigin } from '@highlight/common'
import { useDictatedText } from '../../../../hooks'
import { useActions } from '../../../../providers'
import { useCurrentChatInput, useSetChatInput } from '../../../../store'
import { useIsDictating, useIsVoiceModeActive, useSetIsDictating } from '../../../../store/atoms'
import { logAmplitudeEvent } from '../../../../utils'
import { useSidepanelState } from '@renderer/overlay/atoms'
import type { DownloadState } from '@shared/ipc/GlobalStateUtils'
import { ChatInputElementId } from '../chat-input-element-id'

const dictationOrigin: DictationOrigin = 'assistantInput'

/**
 * Owns the voice mode / dictation lifecycle for the composer's mic button:
 * starting/stopping dictation, folding a finished transcript into the chat
 * input, auto-stopping on sidepanel close or after a minute, and the
 * audio-features-enabled setup delay. Returns only what the button needs to
 * render.
 */
export const useVoiceModeToggle = () => {
  const isVoiceModeActive = useIsVoiceModeActive()
  const { startDictation, stopDictation } = useActions()
  const { dictatedText, clearDictatedText } = useDictatedText(dictationOrigin)
  const chatInput = useCurrentChatInput()
  const setChatInput = useSetChatInput()
  const setIsDictating = useSetIsDictating()
  const isDictating = useIsDictating()
  const [originAwaitingSegment] = useGlobalState<DictationOrigin>('voiceMode.originAwaitingSegment')
  const [whisperDownload] = useGlobalState<DownloadState>('whisper.download')
  const [yamnetDownload] = useGlobalState<DownloadState>('yamnet.download')
  const sidepanelState = useSidepanelState()
  const [areAudioFeaturesEnabled, setAreAudioFeaturesEnabled] = useGlobalStore<boolean>(
    'settings',
    'audio-features-enabled',
    false,
  )
  const [isSettingUpAudio, setIsSettingUpAudio] = useState(false)
  const audioSetupTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearAudioSetupTimeout = useCallback(() => {
    if (audioSetupTimeoutRef.current) {
      clearTimeout(audioSetupTimeoutRef.current)
      audioSetupTimeoutRef.current = null
    }
  }, [])

  const onToggle = useCallback(() => {
    if (!startDictation || !stopDictation) {
      console.log('no startDictation or stopDictation')
      return
    }

    logAmplitudeEvent('Click Chat Input Button', { name: 'Voice' })

    if (isDictating) {
      clearAudioSetupTimeout()
      setIsSettingUpAudio(false)
      stopDictation(dictationOrigin).catch((error) => {
        console.error('Error stopping dictation:', error)
      })
    } else {
      if (!areAudioFeaturesEnabled) {
        setAreAudioFeaturesEnabled(true)
        setIsSettingUpAudio(true)
        audioSetupTimeoutRef.current = setTimeout(() => {
          audioSetupTimeoutRef.current = null
          setIsSettingUpAudio(false)
          startDictation(dictationOrigin).catch((error) => {
            console.error('Error starting dictation:', error)
          })
        }, 3000)
      } else {
        startDictation(dictationOrigin).catch((error) => {
          console.error('Error starting dictation:', error)
        })
      }
    }
    setIsDictating(!isDictating)
  }, [
    isDictating,
    setIsDictating,
    startDictation,
    stopDictation,
    areAudioFeaturesEnabled,
    setAreAudioFeaturesEnabled,
    clearAudioSetupTimeout,
  ])

  useEffect(() => {
    return () => clearAudioSetupTimeout()
  }, [clearAudioSetupTimeout])

  useEffect(() => {
    if (dictatedText && !isDictating) {
      setChatInput(chatInput ? chatInput + ' ' + dictatedText : dictatedText)
      clearDictatedText()
      const wordCount = dictatedText.split(' ').length
      logAmplitudeEvent('Chat Dictation', { wordCount })
      setTimeout(() => {
        GlobalEvents.emit('chatInput:moveCursorToEnd', ChatInputElementId)
      }, 20)
    }
  }, [dictatedText, isDictating, clearDictatedText])

  useEffect(() => {
    if (!isDictating || sidepanelState !== 'hidden' || !stopDictation) return

    clearAudioSetupTimeout()
    setIsSettingUpAudio(false)
    stopDictation(dictationOrigin).catch((error) => {
      console.error('Error stopping dictation after sidepanel close:', error)
    })
    setIsDictating(false)
  }, [isDictating, sidepanelState, stopDictation, setIsDictating, clearAudioSetupTimeout])

  // Auto-stop voice mode after 1 minute
  useEffect(() => {
    if (!isDictating) return

    const timeout = setTimeout(() => {
      if (stopDictation) {
        stopDictation(dictationOrigin).catch((error) => {
          console.error('Error stopping dictation after timeout:', error)
        })
        setIsDictating(false)
      }
    }, 60 * 1000)

    return () => clearTimeout(timeout)
  }, [isDictating, stopDictation, setIsDictating])

  return {
    isVoiceModeActive,
    isDictating,
    isSettingUpAudio,
    isAwaitingSegment: originAwaitingSegment === dictationOrigin,
    isDownloadingModel: whisperDownload?.status === 'downloading' || yamnetDownload?.status === 'downloading',
    onToggle,
  }
}
